import {
  Box,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { SelectInput } from '@components';
import { Button } from '@components/Button';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import {
  getTestLaneResults,
  ScacAccount,
  ScacAccountTypes,
  setTestLaneStore,
  testLaneStore,
} from '@store/customers/customerDetails/LTL';
import { LtlQuoteParametersViewModel } from '@store/orders';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { freightClasses, unitTypes } from '@views/ltl';
import { ZipCodeField } from '@views/ltl/components';
import { QuoteDetailsRowRenderLegacy } from '@views/ltl/components/legacy/QuoteDetailsRowRenderLegacy';
import { QuoteDetailsRowRender } from '@views/ltl/components/quoteDetailsRowRender/QuoteDetailsRowRender';
import { TestLaneFormData } from '@views/ltl/types';
import { useLtlUnifiedQuoteComponent } from '@views/ltl/useLtlUnifiedQuoteComponent';
import { TestLaneSchema } from '@views/ltl/validation';
import {
  Accessor,
  createSignal,
  For,
  Match,
  Setter,
  Show,
  Switch,
} from 'solid-js';

import { TestLaneFormDataClasses as cls } from './classes';

interface TestLaneProps {
  testedLane: Accessor<boolean>;
  setTestedLane: Setter<boolean>;
  scacAccount: ScacAccount;
  customerId: number;
  scacAccountTypes: Accessor<ScacAccountTypes>;
  changeTrackingSource?: ChangeTrackingSource;
}

export const TestLane = (props: TestLaneProps) => {
  const [currentView, setCurrentView] = createSignal<
    'test-form' | 'test-results'
  >('test-form');
  const isUnifiedQuote = useLtlUnifiedQuoteComponent();

  const { form, setFields, data, errors } = createForm<TestLaneFormData>({
    initialValues: {
      originZip: '',
      originCity: '',
      originCountry: 'USA',
      originState: '',
      destinationZip: '',
      destinationCity: '',
      destinationCountry: 'USA',
      destinationState: '',
      lengthInch: null,
      widthInch: null,
      heightInch: null,
      weight: null,
      quantity: null,
      class: '',
      unitType: 'Pallets',
    },
    extend: validator({ schema: TestLaneSchema }),
    onSubmit: async (values) => {
      const ltlQuoteParams: LtlQuoteParametersViewModel = {
        customerId: props.customerId,
        originZip: values.originZip,
        originCity: values.originCity,
        originCountry: values.originCountry,
        originState: values.originState,
        destinationZip: values.destinationZip,
        destinationCity: values.destinationCity,
        destinationCountry: values.destinationCountry,
        destinationState: values.destinationState,
        loadItems: [
          {
            quantity: values.quantity as number,
            class: values.class,
            length: values.lengthInch,
            width: values.widthInch,
            height: values.heightInch,
            weight: values.weight,
            nmfc: '',
            hazmat: false,
            unitType: values.unitType || 'Pallets',
            id: '',
            operationType: 'None',
          },
        ],
        pickupDate: new Date().toISOString(),
        accessorials: [],
        createdByName: '',
        id: 0,
        isLinearFeetOverridden: false,
        isStackable: false,
        linearFeet: 0,
        ltlInsuranceQuote: null,
        operationType: 'None',
        provider: '',
        quoteDate: '',
        quoteRequestLocator: '',
        value: false,
        willExpireSoon: false,
      };
      setTestLaneStore('quoteParameters', ltlQuoteParams);

      const response = await getTestLaneResults(
        props.scacAccount,
        ltlQuoteParams,
        props.changeTrackingSource,
      );

      if (
        !Array.isArray(response) &&
        'results' in response &&
        'failedResults' in response
      ) {
        setTestLaneStore('testResultsSuccess', response.results);
        setTestLaneStore('testResultsFailure', response.failedResults);
      } else {
        setTestLaneStore('testResultsSuccess', []);
        setTestLaneStore('testResultsFailure', []);
      }

      setCurrentView('test-results');
      props.setTestedLane(true);
    },
  });
  const clearZipCode = (type: 'origin' | 'destination') => {
    setFields(`${type}City`, '');
    setFields(`${type}Country`, '');
    setFields(`${type}State`, '');
    setFields(`${type}Zip`, '');
  };
  const onInputOriginAndDestinationZip = (
    e: Event,
    type: 'origin' | 'destination',
  ) => {
    setFields(`${type}City`, '');
    setFields(`${type}Country`, '');
    setFields(`${type}State`, '');
  };

  return (
    <Box>
      <div class={currentView() === 'test-form' ? 'block' : 'hidden'}>
        <Typography variant="h6" sxProps={{ marginBottom: '16px' }}>
          Test Lane
        </Typography>
        <form ref={form}>
          <Grid container spacing={2} rowSpacing={2} columnSpacing={2}>
            <Grid item size={12} spacing={4}>
              <Box width="50%" display="flex" gap={2}>
                <ZipCodeField
                  type="origin"
                  showAddressBookButton={false}
                  customerId={props.customerId}
                  zip={data().originZip}
                  city={data().originCity}
                  state={data().originState}
                  country={data().originCountry}
                  errors={errors()}
                  setFields={setFields}
                  selectedValueBoxClass={cls.selectedValueBox}
                  selectedValueIconClass={cls.selectedValueIcon}
                  onClearZipCode={clearZipCode}
                  onInputZipCode={onInputOriginAndDestinationZip}
                />
                <ZipCodeField
                  type="destination"
                  showAddressBookButton={false}
                  zip={data().destinationZip}
                  city={data().destinationCity}
                  state={data().destinationState}
                  country={data().destinationCountry}
                  customerId={props.customerId}
                  errors={errors()}
                  setFields={setFields}
                  selectedValueBoxClass={cls.selectedValueBox}
                  selectedValueIconClass={cls.selectedValueIcon}
                  onClearZipCode={clearZipCode}
                  onInputZipCode={onInputOriginAndDestinationZip}
                />
              </Box>
            </Grid>
            <Grid item size={2}>
              <TextField
                label="Quantity"
                value={data().quantity ?? ''}
                maxLength={2}
                type="number"
                onChange={(e: unknown) => {
                  setFields('quantity', Number(e as number));
                }}
                on:blur={(e: Event) => {
                  const target = e.target as HTMLInputElement;
                  if (target.value === '') {
                    setFields('quantity', null);
                  }
                }}
                error={errors('quantity')}
                dataTestId="test-lane-quantity-textInput"
              />
            </Grid>
            <Grid item size={2}>
              <SelectInput
                label="Unit Type"
                menuItems={unitTypes.map((u) => ({
                  label: u,
                  value: u,
                }))}
                onChange={(val) => {
                  setFields('unitType', val);
                }}
                value={data().unitType ? data().unitType : 'Pallets'}
                error={((): string | null => {
                  const err = errors('unitType');
                  if (err == null) return null;
                  if (Array.isArray(err)) return err[0] ?? null;
                  return typeof err === 'string' ? err : null;
                })()}
                dataTestId={formatDataTestId(
                  'Test lane unit type',
                  'selectInput',
                )}
              />
            </Grid>
            <Grid item size={1.5}>
              <TextField
                label="Weight"
                type="number"
                maxLength={6}
                error={errors('weight')}
                placeholder="Weight"
                value={data().weight ?? ''}
                dataTestId="weight"
                onChange={(e: unknown) => {
                  setFields('weight', e as number);
                }}
              />
            </Grid>
            <Grid item size={1.5}>
              <TextField
                label="Length"
                maxLength={3}
                type="number"
                error={errors('lengthInch')}
                value={data().lengthInch ?? ''}
                onChange={(e: unknown) => {
                  setFields('lengthInch', Number(e));
                }}
                dataTestId="test-lane-length-textInput"
              />
            </Grid>
            <Grid item size={1.5}>
              <TextField
                label="Width"
                maxLength={3}
                type="number"
                error={errors('widthInch')}
                value={data().widthInch ?? ''}
                onChange={(e: unknown) => {
                  setFields('widthInch', Number(e));
                }}
                dataTestId="test-lane-width-textInput"
              />
            </Grid>
            <Grid item size={1.5}>
              <TextField
                label="Height"
                type="number"
                error={errors('heightInch')}
                maxLength={3}
                value={data().heightInch ?? ''}
                onChange={(e: unknown) => {
                  setFields('heightInch', Number(e));
                }}
                dataTestId="test-lane-height-textInput"
              />
            </Grid>
            <Grid item size={2}>
              <SelectInput
                label="Class"
                value={data().class.toString()}
                onChange={(val) => {
                  setFields('class', val);
                }}
                error={((): string | null => {
                  const err = errors('class');
                  if (err == null) return null;
                  if (Array.isArray(err)) return err[0] ?? null;
                  return typeof err === 'string' ? err : null;
                })()}
                menuItems={freightClasses.map((fc) => ({
                  label: fc,
                  value: fc,
                }))}
                dataTestId={formatDataTestId(
                  'Test lane freight class',
                  'selectInput',
                )}
              />
            </Grid>

            <Grid item size={12}>
              <Box style={{ display: 'flex', 'justify-content': 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  isLoading={testLaneStore.isLoading}
                  disabled={testLaneStore.isLoading}
                  label="Test"
                  dataTestId="customer-details-test-lane-submit-button"
                />
              </Box>
            </Grid>
          </Grid>
        </form>
      </div>

      <div class={currentView() === 'test-results' ? 'block' : 'hidden'}>
        <For each={testLaneStore.testResultsSuccess}>
          {(quote) => {
            return (
              <Box>
                <Switch>
                  <Match when={isUnifiedQuote() === true}>
                    <QuoteDetailsRowRender
                      variant="testLane"
                      quote={quote}
                      customerId={props.customerId}
                    />
                  </Match>
                  <Match when={isUnifiedQuote() === false}>
                    <QuoteDetailsRowRenderLegacy
                      quoteParameters={testLaneStore.quoteParameters!}
                      quote={quote}
                      isHistory={false}
                      customerId={props.customerId}
                      testMode={true}
                    />
                  </Match>
                </Switch>
              </Box>
            );
          }}
        </For>
        <Show when={testLaneStore.testResultsFailure.length > 0}>
          <Typography variant="h6" sxProps={{ marginBottom: '16px' }}>
            Failed Quotes
          </Typography>
          <For each={testLaneStore.testResultsFailure}>
            {(failedQuote) => {
              const serviceType = props
                .scacAccountTypes()
                .serviceLevelTypes.find(
                  (st) => st.id === failedQuote.serviceLevelTypeId,
                );

              const pricingType = props
                .scacAccountTypes()
                .pricingRequestTypes.find(
                  (pt) => pt.id === failedQuote.pricingRequestTypeId,
                );

              return (
                <Paper elevation={1} class="p-1.5 mb-1.5">
                  <Typography variant="body2">
                    <strong>Service:</strong>{' '}
                    {serviceType?.displayName ?? 'N/A'} —{' '}
                    <strong>Pricing:</strong>{' '}
                    {pricingType?.description ?? 'N/A'}
                  </Typography>

                  <Typography variant="body2" sxProps={{ color: '#d32f2f' }}>
                    <strong>Reason:</strong> {failedQuote.message}
                    {'; '}
                    {failedQuote.messageDetails}
                  </Typography>
                </Paper>
              );
            }}
          </For>
        </Show>
        <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
          <Button
            onClick={() => setCurrentView('test-form')}
            variant="contained"
            color="primary"
            label="Back"
            dataTestId="customer-details-test-lane-back-button"
          />
        </Box>
      </div>
    </Box>
  );
};
